import { useState, useRef, useEffect } from "react";
import styled from "styled-components"
import { useTranslation } from 'react-i18next';
import { purpleColors, violetColors, slateColors, Section, Container, fontFamily } from './AppLandingPage/styles';
import { Helmet } from 'react-helmet-async';
import { CONTACT_SEO } from '../../seo/pageSeoMeta';
import { ArrowRight } from 'lucide-react';
import { postApi } from "../../utils/api";
import { TextField, MultiLineTextField } from '../Controls/TextFields';

const HeroSection = styled(Section)`
  position: relative;
  overflow: hidden;
  background: linear-gradient(to bottom right, ${slateColors[50]}, white, ${purpleColors[50]});
  padding: 3rem 1.5rem;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, ${purpleColors[500]}08 1px, transparent 1px),
      linear-gradient(to bottom, ${purpleColors[500]}08 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
`;

const HeroContent = styled(Container)`
  position: relative;
  z-index: 10;
  max-width: 48rem;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1rem;
  line-height: 1.1;
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 4rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  margin: 0 auto;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FormSection = styled(Section)`
  background: white;
`;

const FormContainer = styled(Container)`
  max-width: 42rem;
`;

const FormWrapper = styled.div`
display: flex;
flex-direction: column;
  gap: 1.25rem;
`;


const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
display: flex;
  align-items: center;
justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  
  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${violetColors[600]});
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const SuccessMessage = styled.div`
  padding: 1.5rem;
  background: ${slateColors[50]};
  border-radius: 0.75rem;
  border: 2px solid ${slateColors[200]};
  text-align: center;
`;

const SuccessText = styled.p`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin: 0;
`;

const ErrorMessage = styled.div`
  padding: 1.5rem;
  background: #fee2e2;
  border-radius: 0.75rem;
  border: 2px solid #fecaca;
  text-align: center;
`;

const ErrorTextMessage = styled.p`
  font-size: 1rem;
  font-weight: 500;
  color: #dc2626;
  margin: 0;
`;

const ContactPage = () => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [isNameError, setIsNameError] = useState(false);
    const [isEmailError, setIsEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState("");
    const [isMessageError, setIsMessageError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const getAccessRef = useRef<HTMLDivElement>(null);

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
  
    const handleSubmit = async () => {
      let hasError = false;
  
      if (!name) {
        setIsNameError(true);
        hasError = true;
      } else {
        setIsNameError(false);
      }
  
      if (!email) {
        setEmailErrorMessage(t('common.thisFieldRequired'));
        setIsEmailError(true);
        hasError = true;
      } else if (!validateEmail(email)) {
        setEmailErrorMessage(t('common.validEmailRequired'));
        setIsEmailError(true);
        hasError = true;
      } else {
            setIsEmailError(false);
      }
  
      if (!message) {
        setIsMessageError(true);
        hasError = true;
      } else {
        setIsMessageError(false);
      }
  
      if (!hasError) {
        const formData = {
            name,
            email,
            message,
        };
  
      try {
        const response = await postApi('send-contact-email', formData);
  
        if (response.ok) {
          const data = await response.json();
          console.log(data.message);
          setIsSuccess(true);
          setName('');
          setEmail('');
          setMessage('');
          setErrorMessage("");
        } else {
          const errorData = await response.json();
          console.error('Error:', errorData.message);
          setErrorMessage(errorData.message);
        }
      } catch (error) {
        console.error('Network error:', error);
        setErrorMessage(t('common.networkError'));
      }
    }
  };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
      <>
        <Helmet prioritizeSeoTags>
          <title>{CONTACT_SEO.title}</title>
          <meta name="description" content={CONTACT_SEO.description} />
          <meta name="keywords" content="contact chuuchuu, train delay data contact, rail analytics support, train data support" />
          <meta name="prerender-status-code" content="200" />
          <meta name="prerender-header" content="Cache-Control: max-age=300, must-revalidate" />
          <meta name="fragment" content="!" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://chuuchuu.com/contact" />
          <meta property="og:title" content={CONTACT_SEO.title} />
          <meta property="og:description" content={CONTACT_SEO.description} />
          <meta property="og:image" content="https://chuuchuu.com/images/previewpage.jpg" />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://chuuchuu.com/contact" />
          <meta property="twitter:title" content={CONTACT_SEO.title} />
          <meta property="twitter:description" content={CONTACT_SEO.description} />
          <meta property="twitter:image" content="https://chuuchuu.com/images/previewpage.jpg" />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://chuuchuu.com/contact" />
        </Helmet>

        <HeroSection>
          <HeroContent>
            <HeroTitle>{t('contact.title')}</HeroTitle>
            <HeroDescription>
              {t('contact.description')}
            </HeroDescription>
          </HeroContent>
        </HeroSection>

        <FormSection ref={getAccessRef}>
                    <FormContainer>
            {!isSuccess ? (
              <FormWrapper>
                <TextField
                  instruction={t('common.name')}
                  dynamicPlaceholder={t('contact.namePlaceholder')}
                  isDisabled={false}
                  isError={isNameError}
                  errorMessage={t('common.thisFieldRequired')}
                  value={name}
                  isBackgroundDark={false}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (isNameError) setIsNameError(false);
                  }}
                />
                <TextField
                  instruction={t('common.email')}
                  dynamicPlaceholder={t('contact.emailPlaceholder')}
                  isDisabled={false}
                  isError={isEmailError}
                  errorMessage={emailErrorMessage}
                  value={email}
                  isBackgroundDark={false}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (isEmailError) setIsEmailError(false);
                  }}
                />
                <MultiLineTextField
                  instruction={t('common.message')}
                  dynamicPlaceholder={t('contact.messagePlaceholder')}
                  isDisabled={false}
                  isError={isMessageError}
                  errorMessage={t('common.thisFieldRequired')}
                  value={message}
                  isBackgroundDark={false}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (isMessageError) setIsMessageError(false);
                  }}
                />
                <SubmitButton onClick={handleSubmit}>
                  {t('common.send')}
                  <ArrowRight />
                </SubmitButton>
                {errorMessage && (
                        <ErrorMessage>
                    <ErrorTextMessage>{errorMessage}</ErrorTextMessage>
                        </ErrorMessage>
                    )}
              </FormWrapper>
            ) : (
                <SuccessMessage>
                <SuccessText>{t('contact.successMessage')}</SuccessText>
                </SuccessMessage>
            )}
          </FormContainer>
        </FormSection>
      </>
    );
};
  
export default ContactPage;
